import React from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Chip, Button } from '@mui/material';
import {
    CurrencyRupee,
    DateRange,
    Group,
    Sell,
    Person,
    AccessTimeFilled,
    StopCircle,
    Percent,
    MoreVert,
    Delete,
    GridView
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { CalendarIcon } from '../../icons/FontAwesomeIcon';
import Menus from '../Dialog/Menus';
import { TICKET_STATUS } from '../../app/store/constant';
import { useEventStatus } from '../../hooks/event-hooks/useEventStatus';
import InfinityLoader from '../../animation/InfinityLoader';
import { H4, Body3, H5, Body2 } from '../../themes/typography';
import '../../styles/components/ManageTicket.css';
import '../../styles/components/UserTicket.css';

function ManageTicket({
    ticketName = 'Community Workshop Access',
    ticketId = 'TKT-2025-0042',
    ticketDescription = 'Exclusive access to all areas including backstage, premium seating, and complimentary refreshments. Early entry privileges and post-event meet and greet with artists.',
    price = '₹299.99',
    soldTicketQty = 156,
    totalTicketQty = 200,
    gstRate,
    totalSales = '₹478541.00',
    entryPerTicket = '5',
    salePeriodStartDate = 'Mon 09 Dec, 10:00 AM',
    salePeriodEndDate = 'Wed 11 Dec, 11:59 PM',
    ticketSchedule = [],
    ticketStatus = 'Scheduled',
    exceedStatus = false,
    onMarkAsSoldOut = () => {},
    onEndSale = () => {},
    onDelete = () => {},
    onViewTicket = () => {}
}) {
    // console.log("Ticket Schedule: ", ticketSchedule)
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const { eventData } = useSelector((state) => state.event);

    const { isEventCancelled } = useEventStatus();

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleClose() {
        setAnchorEl(null);
    }

    // Menu actions
    const menusList = [
        {
            id: 'view-ticket',
            icon: <GridView color="tertiary" />,
            menuName: 'View ticket',
            onClick: () => onViewTicket(ticketId)
        },
        {
            id: 'delete-ticket',
            icon: <Delete color="tertiary" />,
            menuName: 'Delete ticket',
            onClick: () => onDelete(ticketId)
        }
    ];

    /**
     * Function to handle the chip variant based on ticket status
     * Assigned chip variants for different ticket statuses
     */
    function getChipVariant(status) {
        switch (status) {
            case TICKET_STATUS.live:
                return 'live';
            case TICKET_STATUS.saleEnded:
            case TICKET_STATUS.soldOut:
                return 'soldOut';
            case TICKET_STATUS.scheduled:
            case TICKET_STATUS.commingSoon:
            default:
                return 'scheduled';
        }
    }

    function getStatusLabel(status) {
        switch (status) {
            case TICKET_STATUS.live:
                return 'Live';
            case TICKET_STATUS.soldOut:
                return 'Sold Out';
            case TICKET_STATUS.saleEnded:
                return 'Sale Ended';
            // case TICKET_STATUS.commingSoon:
            //     return 'Coming Soon';
            // case TICKET_STATUS.scheduled:
            default:
                return 'Scheduled';
        }
    }

    return (
        <React.Fragment>
            <div className="manage-ticket-main-container">
                <div className="manage-ticket-wrapper">
                    {/* Ticket Basic Details Container */}
                    <div className="ticket-manage-details">
                        <div className="ticket-name-status-container">
                            <div className="ticket-name">
                                <H4 fontWeight={700} color="#f8a21e">
                                    {ticketName}
                                </H4>
                            </div>
                            <Chip
                                label={getStatusLabel(ticketStatus)}
                                variant={getChipVariant(ticketStatus)}
                            />
                        </div>

                        {/* <div className='ticket-id-label-container'>
                            <Body3>ID :</Body3>
                            <Body3 fontWeight={500}>{ticketId}</Body3>
                        </div> */}

                        <div className="ticket-description-container">
                            <Body2 fontWeight={400}>{ticketDescription}</Body2>
                        </div>

                        <div className="elipsis-ticket-menu">
                            <IconButton
                                color="elipsis"
                                onClick={handleClick}
                                aria-controls={open ? 'ticket-actions' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <MoreVert />
                            </IconButton>
                            <Menus
                                id="ticket-menus"
                                open={open}
                                anchorEl={anchorEl}
                                onClick={handleClose}
                                onClose={handleClose}
                                menus={menusList}
                                shouldDisabledMenus={
                                    ticketStatus === TICKET_STATUS.soldOut ||
                                    ticketStatus === TICKET_STATUS.saleEnded ||
                                    isEventCancelled
                                }
                            />
                        </div>
                    </div>

                    {/* Ticket Sales Details Container */}
                    <div className="sales-manage-details">
                        <div className="sale-field-container">
                            <div className="sale-icon-container">
                                <CurrencyRupee sx={iconStyle} />
                            </div>
                            <div className="sale-label-value-container">
                                <Body3>Price</Body3>
                                <H5 fontWeight={700}>{price === '0.00' ? 'Free' : price}</H5>
                            </div>
                        </div>

                        <div className="sale-field-container">
                            <div className="sale-icon-container">
                                <Group sx={iconStyle} />
                            </div>
                            <div className="sale-label-value-container">
                                <Body3>Sold / Total</Body3>
                                <div className="exceed-case">
                                    <H5 fontWeight={700}>
                                        {soldTicketQty} / {totalTicketQty}
                                    </H5>
                                    {exceedStatus && (
                                        <div className="infinity-icon">
                                            <InfinityLoader />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="sale-field-container">
                            <div className="sale-icon-container">
                                <Percent sx={iconStyle} />
                            </div>
                            <div className="sale-label-value-container">
                                <Body3>GST Applicable (%)</Body3>
                                <H5 fontWeight={700}>
                                    {gstRate?.id === 1 ? 'No' : gstRate?.igst_rate}
                                </H5>
                            </div>
                        </div>

                        <div className="sale-field-container">
                            <div className="sale-icon-container">
                                <Sell sx={iconStyle} />
                            </div>
                            <div className="sale-label-value-container">
                                <Body3>Total Sales</Body3>
                                <H5 fontWeight={700}>{totalSales}</H5>
                            </div>
                        </div>
                    </div>

                    <div className="separator-of-ticket"></div>

                    <div className="sale-period-details">
                        <div className="period-box-width">
                            <div className="sale-period-icon-label">
                                <div style={iconTopPadding}>
                                    <DateRange sx={iconStyle2} />
                                </div>
                                <Body2 fontWeight={300}>Sale period : </Body2>
                                <Body2 fontWeight={400}>
                                    {dayjs(salePeriodStartDate, 'YYYY-MM-DD HH:mm').format(
                                        'ddd DD MMM, hh:mm A'
                                    )}{' '}
                                    to{' '}
                                    {dayjs(salePeriodEndDate, 'YYYY-MM-DD HH:mm').format(
                                        'ddd DD MMM, hh:mm A'
                                    )}
                                </Body2>
                            </div>
                            <div className="sale-period-icon-label">
                                <div style={iconTopPadding}>
                                    <AccessTimeFilled sx={iconStyle2} />
                                </div>
                                <Body2 fontWeight={300}>Ticket valid for :</Body2>
                                {eventData?.event_type === 'continuous' ? (
                                    <Body2 fontWeight={400}>
                                        {dayjs(eventData?.start_date).format('ddd DD MMM')}{' '}
                                        {dayjs(`1970-01-01T${eventData?.start_time}`).format(
                                            'HH:mm'
                                        )}{' '}
                                        - {dayjs(eventData?.end_date).format('ddd DD MMM')}{' '}
                                        {dayjs(`1970-01-01T${eventData?.end_time}`).format('HH:mm')}
                                    </Body2>
                                ) : ticketSchedule?.length === 1 ? (
                                    <Body2 fontWeight={400}>
                                        {dayjs(ticketSchedule[0]?.date).format('ddd DD MMM')}{' '}
                                        {dayjs(`1970-01-01T${ticketSchedule[0]?.from_time}`).format(
                                            'HH:mm'
                                        )}{' '}
                                        to{' '}
                                        {dayjs(`1970-01-01T${ticketSchedule[0]?.to_time}`).format(
                                            'HH:mm'
                                        )}
                                    </Body2>
                                ) : (
                                    ticketSchedule?.map((schedule, index) => (
                                        <div style={multiDateContainer} key={index}>
                                            <div style={calenderContainer}>
                                                <CalendarIcon
                                                    style={{
                                                        color: '#26C1E8',
                                                        fontSize: '0.825rem'
                                                    }}
                                                />
                                            </div>
                                            <div style={dateContainer}>
                                                <Body2>
                                                    {dayjs(schedule?.date).format('ddd DD MMM')}
                                                </Body2>
                                                <Body3>
                                                    {dayjs(
                                                        `1970-01-01T${schedule?.from_time}`
                                                    ).format('HH:mm')}{' '}
                                                    to{' '}
                                                    {dayjs(
                                                        `1970-01-01T${schedule?.to_time}`
                                                    ).format('HH:mm')}
                                                </Body3>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="ticket-manage-footer">
                        <div className="sale-period-icon-label">
                            <div style={iconTopPadding}>
                                <Person sx={iconStyle2} />
                            </div>
                            <Body2 fontWeight={300}>Entry per ticket : </Body2>
                            <Body2 fontWeight={400}>{entryPerTicket} Person</Body2>
                        </div>
                        <div className="ticket-actions-buttons-container">
                            <Button
                                startIcon={<Sell />}
                                variant="markAsSoldOut"
                                disabled={
                                    ticketStatus === TICKET_STATUS.soldOut ||
                                    ticketStatus === TICKET_STATUS.saleEnded ||
                                    isEventCancelled
                                }
                                onClick={() => onMarkAsSoldOut()}
                            >
                                Mark as Sold Out
                            </Button>
                            <Button
                                startIcon={<StopCircle />}
                                sx={endSaleBtn}
                                onClick={() => onEndSale()}
                                disabled={
                                    ticketStatus === TICKET_STATUS.soldOut ||
                                    ticketStatus === TICKET_STATUS.saleEnded ||
                                    isEventCancelled
                                }
                            >
                                End Sale
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default ManageTicket;

const iconStyle = {
    color: '#f8a21e',
    fontSize: '1.5rem'
};

const iconStyle2 = {
    color: '#f8a21e',
    fontSize: '1.125rem'
};

const iconTopPadding = {
    paddingTop: '0.1rem'
};

const endSaleBtn = {
    border: '1px solid #f8a21e',
    color: '#f8a21e',
    backgroundColor: 'rgba(248, 162, 30, 0.15)'
};

const multiDateContainer = {
    display: 'flex',
    height: '42px'
};

const calenderContainer = {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '0.25rem',
    width: '30px',
    height: '100%',
    justifyContent: 'center'
};

const dateContainer = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
};
